import Col from "antd/es/col";
import Row from "antd/es/row";
import AntForm from "antd/es/form";
import { PasswordSection } from "features/auth/PasswordSection";
import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import {
  LARGE_MOBILE,
  MOBILE_SEARCH,
  REGISTER_VERIFY_EMAIL_PATH,
} from "common/constants";
import React, { useContext, useState } from "react";
import { useGeetest } from "utils/hooks/useGeetest";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { LanguageNameSpaces, RegisterRequest } from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import styled, { css, useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import useMedia from "utils/hooks/useMedia";
import { PrimaryTitle } from "./styles";
import {
  EMAIL_INPUT_PROPS,
  getEmailFormItemRules,
} from "features/auth/constants";
import AuthFormFooter from "features/auth/AuthFormFooter";
import AuthInlineError, {
  useAuthResultMessage,
} from "features/auth/AuthInlineError";
import useRegisterForm from "@shared/features/onboarding/hooks/useRegisterForm";
import {
  EMAIL_UUID_QUERY_PARAM,
  PRIVACY_POLICY_LINK,
  REGISTERED_EMAIL,
  TERMS_OF_USE_LINK,
} from "@shared/common/constants";
import useQuery from "utils/hooks/useQuery";
import TelegramUserPreview from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/TelegramUserPreview";
import SocialLogins from "features/auth/SocialLogins";
import Link from "common/Typography/Link.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import { useReferralCode } from "features/auth/hooks.ts";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import LoginDescription from "features/configuration/InviteExternalUsers/LoginDescription.tsx";
import { setExternalInviteLocalStorageLogin } from "features/configuration/common/utils.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  AUTH_REGISTER_EMAIL_INPUT_ID,
  AUTH_REGISTER_FIRST_NAME_INPUT_ID,
  AUTH_REGISTER_LAST_NAME_INPUT_ID,
  AUTH_REGISTER_PASSWORD_INPUT_ID,
} from "testIds.ts";

const RegisterForm = () => {
  const { isWhitelabel } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { isNewLook } = useTheme();
  const history = useHistory();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { getUpdatedPath } = useHistoryWithQuery();
  const [form] = AntForm.useForm();
  const location = useLocation();
  const query = useQuery();
  const queryParams = Object.fromEntries(query);
  const isVerifyEmail = !!matchPath(location.pathname, {
    path: REGISTER_VERIFY_EMAIL_PATH,
    exact: true,
    strict: true,
  });
  const referralCode = useReferralCode();
  const {
    display: displayResultMessage,
    error: inlineError,
    clearError,
  } = useAuthResultMessage();

  const [authCode, setAuthCode] = useState<string>();
  const {
    token: externalInviteToken,
    isExternalConnectAccount,
    handleAuthCompleted,
  } = useContext(ExternalConnectAccountContext) || {};

  const {
    onFinish,
    referralDiscount,
    isLoading,
    checkReferral,
    telegramUserData,
    removeUuid,
  } = useRegisterForm({
    displayResultMessage,
    isVerifyEmail,
    referralCode,
    queryParams,
    onSuccess: (email, emailValidatorUUID) => {
      sessionStorage.setItem(REGISTERED_EMAIL, email);
      if (externalInviteToken) {
        handleAuthCompleted?.();
      } else {
        setExternalInviteLocalStorageLogin();
      }
      if (emailValidatorUUID) {
        history.push(
          getUpdatedPath(
            { [EMAIL_UUID_QUERY_PARAM]: emailValidatorUUID },
            undefined,
            REGISTER_VERIFY_EMAIL_PATH
          )
        );
      }
    },
  });

  const { verify: verifyCaptcha, isLoading: isCaptchaLoading } = useGeetest({
    onSuccess: (captcha_token) => {
      const registerRequest: RegisterRequest = {
        ...form.getFieldsValue(),
        captcha_token: captcha_token,
        is_whitelabel: isWhitelabel,
        auth_code: authCode,
        client_token: externalInviteToken,
      };
      onFinish(registerRequest).catch(() => {});
    },
    maxNumCaptchaRefetch: 3,
  });

  const referralCodeMessage = !!referralDiscount && (
    <ReferralCodeMessageWrapper $isMobile={isMobile}>
      <BoldText type="success">{t("register.referral_title")}</BoldText>
      <Text type="success">
        {t("register.referral_description", {
          discount: toFixed(referralDiscount * 100, 2),
        })}
      </Text>
    </ReferralCodeMessageWrapper>
  );

  const _isLoading = isLoading || isCaptchaLoading;

  const telegramUserPreview = telegramUserData && (
    <TelegramUserPreview
      horizontal={true}
      showTelegramIcon={true}
      showRemoveUuidButton={true}
      showInfoButton={true}
      telegramUser={telegramUserData}
      style={{ marginBottom: 16 }}
      removeUuid={removeUuid}
    />
  );

  const onSubmitClick = (authCode?: string) => {
    if (authCode) setAuthCode(authCode);
    verifyCaptcha();
  };

  const getTitle = () => {
    if (isExternalConnectAccount) {
      return <LoginDescription />;
    }
    if (isNewLook) {
      return (
        <PrimaryTitle $isMobile={isMobile}>
          {t("register.title_new_look")}
        </PrimaryTitle>
      );
    }
    return (
      <PrimaryTitle $isMobile={isMobile} style={{ marginBottom: 24 }}>
        {t("register.welcome")}
        <br />
        {t("register.title")}
      </PrimaryTitle>
    );
  };

  return (
    <StyledForm
      name="register"
      form={form}
      initialValues={{
        remember: true,
      }}
      onFinish={() => onSubmitClick()}
      onValuesChange={clearError}
      layout="vertical"
      requiredMark={false}
    >
      {getTitle()}
      {referralCodeMessage}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="first_name"
            label={t(
              isNewLook ? "fields.first_name_new_look" : "fields.first_name"
            )}
            rules={[
              {
                required: true,
                message: t("validation.first_name_required"),
              },
            ]}
          >
            <Input
              size="large"
              data-testid={AUTH_REGISTER_FIRST_NAME_INPUT_ID}
              autoComplete="given-name"
              autoCapitalize="words"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="last_name"
            label={t(
              isNewLook ? "fields.last_name_new_look" : "fields.last_name"
            )}
            rules={[
              {
                required: true,
                message: t("validation.last_name_required"),
              },
            ]}
          >
            <Input
              size="large"
              data-testid={AUTH_REGISTER_LAST_NAME_INPUT_ID}
              autoComplete="family-name"
              autoCapitalize="words"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="email"
        label={t("fields.email")}
        rules={getEmailFormItemRules(t)}
      >
        <Input
          size="large"
          data-testid={AUTH_REGISTER_EMAIL_INPUT_ID}
          {...EMAIL_INPUT_PROPS}
        />
      </Form.Item>
      <PasswordSection
        size="large"
        showPlaceholder={false}
        passwordInputTestId={AUTH_REGISTER_PASSWORD_INPUT_ID}
        autoComplete="new-password"
      />
      {checkReferral && (
        <Form.Item
          name="referral"
          label={t(
            isNewLook ? "fields.referral_code_new_look" : "fields.referral_code"
          )}
        >
          <Input placeholder={referralCode} size="large" disabled={true} />
        </Form.Item>
      )}
      {telegramUserPreview}
      <TermsText $size="S" style={{ marginTop: isNewLook ? 16 : 20 }}>
        {t("register.terms_intro")}{" "}
        <Link
          href={TERMS_OF_USE_LINK}
          target="blank"
          $size={typography.fontSizes.S}
        >
          {t("register.terms_of_use")}
        </Link>{" "}
        {t("register.and")}{" "}
        <Link
          href={PRIVACY_POLICY_LINK}
          target="blank"
          $size={typography.fontSizes.S}
        >
          {t("register.privacy_policy")}
        </Link>
      </TermsText>
      <AuthInlineError message={inlineError} style={{ margin: "16px 0 0" }} />
      <AuthFormFooter
        buttonLoading={_isLoading}
        buttonStyle={{ marginTop: isNewLook ? (inlineError ? 16 : 24) : 20 }}
        isRegister={true}
      />
      <SocialLogins isRegister={true} />
    </StyledForm>
  );
};

export default RegisterForm;

const ReferralCodeMessageWrapper = styled(Flex)<{ $isMobile: boolean }>`
  flex-direction: column;
  background: ${(p) => hexToRGB(p.theme.colors.lightGreen, 0.2)};
  padding: ${(p) => (p.$isMobile ? "8px 4px" : "8px 16px")};
  margin-bottom: 24px;
  text-align: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: flex-start;
      gap: 2px;
      padding: 10px 12px;
      border-radius: ${p.theme.radii.control}px;
      border: 1px solid ${hexToRGB(p.theme.colors.green, 0.3)};
      background: ${hexToRGB(
        p.theme.colors.green,
        p.theme.isDarkMode ? 0.1 : 0.05
      )};
      text-align: start;
    `}
`;
const TermsText = styled(Text)`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      ${newLookType.meta};
      color: ${getNewLookTokens(p.theme).muted};
      a {
        font-size: 12px;
      }
      /* Phones: legal copy stays readable at arm's length. */
      @media (max-width: ${LARGE_MOBILE}) {
        font-size: 13px;
        line-height: 18px;
        a {
          font-size: 13px;
        }
      }
    `}
`;
const BoldText = styled(Text)`
  font-weight: ${(p) =>
    p.theme.isNewLook ? 600 : p.theme.typography.fontWeights.BOLD};
`;
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }
`;
